import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ErrorOutlineOutlined } from "@mui/icons-material";
import { ScreenerImportRowResult } from "./screenerImportRowResults";

type Props = {
  rows: ScreenerImportRowResult[];
  maxHeight?: number;
  title?: string;
  subtitle?: string;
};

const ScreenerImportFailedRowsTable = ({
  rows,
  maxHeight = 280,
  title = "Failed rows",
  subtitle,
}: Props) => {
  if (!rows.length) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: "hidden",
        borderColor: "error.light",
        bgcolor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(211, 47, 47, 0.06)"
            : "rgba(211, 47, 47, 0.03)",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
          <ErrorOutlineOutlined color="error" fontSize="small" />
          <Box minWidth={0}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" display="block">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        <Chip
          size="small"
          color="error"
          variant="outlined"
          label={`${rows.length.toLocaleString()} row${rows.length === 1 ? "" : "s"}`}
        />
      </Stack>

      <Box sx={{ maxHeight, overflow: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 72, fontWeight: 600, bgcolor: "background.paper" }}>
                Row
              </TableCell>
              <TableCell sx={{ minWidth: 160, fontWeight: 600, bgcolor: "background.paper" }}>
                Email
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper" }}>
                Reason
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const errorText = row.error?.trim() || "Unknown error";
              return (
                <TableRow
                  key={`${row.row}-${row.email}-${index}`}
                  hover
                  sx={{
                    "&:nth-of-type(even)": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {row.row}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Tooltip title={row.email || "—"} placement="top-start">
                      <Box component="span">{row.email || "—"}</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ color: "error.main" }}>
                    <Tooltip title={errorText} placement="top-start">
                      <Typography
                        variant="body2"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {errorText}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};

export default ScreenerImportFailedRowsTable;
